import React, { useEffect, useRef, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  HeadlineCard as HeadlineCardBox,
  GaugeCol, Gauge, GaugeTrack, GaugeFill, GaugeCenter, GaugeScore, GaugeOf,
  HeadlineContent, HeadlineEyebrow, CategoryRow, CategoryIconBox, CategoryName, CategoryDesc,
  TldrText,
} from '../styles';

interface Props {
  accent: string;
  score: number;
  /** Optional secondary line for gauge center, e.g. "/ 100 RISC". */
  scoreLabel?: string;
  eyebrow: string;
  icon: LucideIcon;
  category: string;
  /** Inline descriptor next to category (already composed with separators). */
  descriptor?: React.ReactNode;
  /** Optional TL;DR paragraph (Merriweather italic). */
  tldr?: React.ReactNode;
}

/** Animated count-up — runs once on mount & on score change. */
function useCountUp(target: number, duration = 900, delay = 1100): number {
  const [val, setVal] = useState(0);
  const rafRef = useRef<number | null>(null);
  useEffect(() => {
    let start: number | null = null;
    const tick = (ts: number) => {
      if (start == null) start = ts;
      const t = Math.min(1, (ts - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(Math.round(target * eased));
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    const timer = setTimeout(() => { rafRef.current = requestAnimationFrame(tick); }, delay);
    return () => {
      clearTimeout(timer);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [target, duration, delay]);
  return val;
}

export const HeadlineCard: React.FC<Props> = ({
  accent, score, scoreLabel, eyebrow, icon: Icon, category, descriptor, tldr,
}) => {
  const animated = useCountUp(score);
  void animated; // gauge fills via CSS keyframe; static value used; reserved for future
  return (
    <HeadlineCardBox accent={accent}>
      <GaugeCol>
        <Gauge accent={accent}>
          <svg viewBox="0 0 168 168">
            <GaugeTrack cx={84} cy={84} r={80} />
            <GaugeFill cx={84} cy={84} r={80} scorePercent={score} />
          </svg>
          <GaugeCenter>
            <GaugeScore>{score}</GaugeScore>
            {scoreLabel && <GaugeOf>{scoreLabel}</GaugeOf>}
          </GaugeCenter>
        </Gauge>
      </GaugeCol>
      <HeadlineContent>
        <HeadlineEyebrow>{eyebrow}</HeadlineEyebrow>
        <CategoryRow>
          <CategoryIconBox accent={accent}>
            <Icon size={18} strokeWidth={2.2} />
          </CategoryIconBox>
          <CategoryName>{category}</CategoryName>
          {descriptor && <CategoryDesc>{descriptor}</CategoryDesc>}
        </CategoryRow>
        {tldr && <TldrText>{tldr}</TldrText>}
      </HeadlineContent>
    </HeadlineCardBox>
  );
};
